import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Grid from '../components/Grid'; 
import { FaEye, FaDownload, FaTimes } from "react-icons/fa"; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { DataGrid } from '@mui/x-data-grid';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function SupplierPayments({ onAddPaymentClick }) {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedSearchField, setSelectedSearchField] = useState('payment_receipt_id');

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) return toast.error('Authentication token is missing.');
        
        const response = await axios.get(`${API_BASE_URL}/supplierPayments/getAll`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const sortedPayments = response.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setPayments(sortedPayments);

      } catch (error) {
        console.error('Error fetching supplier payments:', error);
        toast.error('Failed to fetch supplier payments');
      }
    };
    fetchPayments();
  }, []);

  const filteredPayments = payments.filter((payment) => {
    const term = searchTerm.toLowerCase();

    switch (selectedSearchField) {
        case 'supplier_payment_id':
        return (payment?.supplier_payment_id || "").toLowerCase().includes(term);

        case 'amount':
        return payment?.amount?.toString().includes(term);

        case 'date':
        return new Date(payment?.createdAt || "").toLocaleDateString().includes(term);

        case 'status':
        return (payment?.status || "").toLowerCase().includes(term);

        case 'supplier_id':
        return (payment?.supplier_id?.supplier_code || "").toLowerCase().includes(term);

        case 'createdBy':
        return (payment?.createdBy?.username || "").toLowerCase().includes(term);

        case 'supplier_name':
        return (payment?.supplier_id?.supplier_name || "").toLowerCase().includes(term);

        default:
        return true;
    }
    });

  const handleDownloadExcel = async () => {
    const dataToExport = filteredPayments.length ? filteredPayments : payments;
    if (!dataToExport.length) return toast.warn("No payment data to export.");

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Supplier Payments");

    worksheet.addRow([
      "Payment Receipt ID",
      "Supplier Code",
      "Supplier Name",
      "Date",
      "Amount (LKR)",
      "Status",
      "Created By"
    ]);

    worksheet.getRow(1).eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD966" } };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" }, left: { style: "thin" },
        bottom: { style: "thin" }, right: { style: "thin" }
      };
    });

    dataToExport.forEach(payment => {
      worksheet.addRow([
        payment.supplier_payment_id,
        payment.supplier_id?.supplier_code || "N/A",
        payment.supplier_id?.supplier_name || "N/A",
        new Date(payment.createdAt).toLocaleDateString(),
        Number(payment.amount).toFixed(2),
        payment.status,
        payment.createdBy?.username || "Unknown"
      ]);
    });

    worksheet.columns.forEach(column => { column.width = 20; });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "supplier_payments.xlsx");
  };

  const fetchPaymentDetails = async (_id) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/supplierPayments/getById/${_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedPayment(response.data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to fetch payment details');
    }
  };

  const handleCancelPayment = async (supplier_payment_id) => {
    if (!window.confirm("Are you sure you want to cancel this payment?")) return;

    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/supplierPayments/cancel/${supplier_payment_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Payment canceled successfully!");
      // Update the row locally
      setPayments((prev) =>
        prev.map((p) =>
          p.supplier_payment_id === supplier_payment_id
            ? { ...p, status: "Canceled" }
            : p
        )
      );
    } catch (error) {
      console.error("Error canceling supplier payment:", error);
      toast.error(error.response?.data?.message || "Failed to cancel payment");
    }
  };

  const columns = [
    { field: 'supplier_payment_id', headerName: 'Payment Receipt ID', flex: 1 },
    { field: 'createdAt', headerName: 'Date', flex: 1,
      renderCell: (params) => new Date(params.value).toLocaleDateString("en-GB")
    },
    { field: 'supplier_code', headerName: 'Supplier Code', flex: 1,
      renderCell: (params) => params.row.supplier_id?.supplier_code || "N/A"
    },
    { field: 'supplier_name', headerName: 'Supplier Name', flex: 1,
      renderCell: (params) => params.row.supplier_id?.supplier_name || "N/A"
    },
    { field: 'amount', headerName: 'Amount (LKR)', flex: 1, align: "right", headerAlign: "right",
      renderCell: (params) => Number(params.value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
    },
    { field: 'status', headerName: 'Status', flex: 1, align: "center", headerAlign: "center",
      renderCell: (params) => {
        const colorMap = {
          "Unutilized": "bg-[#FBC02D]",
          "Partially Utilized": "bg-[#8BC34A]",
          "Fully Utilized": "bg-[#90A4AE]",
        };
        return <span className={`px-2 py-1 rounded-sm text-white ${colorMap[params.value] || "bg-gray-500"}`}>{params.value}</span>;
      }
    },
    { field: 'createdBy', headerName: 'Created By', flex: 1, align: "center", headerAlign: "center",
      renderCell: (params) => params.row.createdBy?.username || "Unknown"
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isSettled = params.row.status === "Partially Utilized" || params.row.status === "Fully Utilized"|| params.row.status === "Canceled";

        return (
          <div className="flex items-center gap-2">
            <button
              className="bg-[#FBC02D] text-white p-2 rounded"
              onClick={() => fetchPaymentDetails(params.row.supplier_payment_id)}
              title="View Payment"
            >
              <FaEye />
            </button>

            <button
              onClick={() => handleCancelPayment(params.row.supplier_payment_id)}
              className={`text-white p-2 flex items-center justify-center rounded ${
                isSettled ? "bg-gray-400 cursor-not-allowed" : "bg-[#d82828] hover:bg-red-700"
              }`}
              title={isSettled ? "Cannot cancel utilized payment" : "Cancel Payment"}
              disabled={isSettled}
            >
              <FaTimes />
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Supplier Payments</h1>
          <button
            onClick={onAddPaymentClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add Payment
          </button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="supplier_payment_id">Payment Receipt ID</option>
                <option value="amount">Amount</option>
                <option value="date">Date</option>
                <option value="status">Status</option>
                <option value="supplier_id">Supplier Code</option>
                <option value="createdBy">Created By</option>
                <option value="supplier_name">Supplier Name</option>
              </select>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Payments"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            <button
              onClick={handleDownloadExcel}
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <FaDownload className="mr-2" />
              Download Excel
            </button>
          </div>

          <Grid rows={filteredPayments} columns={columns} getRowId={(row) => row.supplier_payment_id} />

          {selectedPayment && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
              <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
                <button
                  onClick={() => setSelectedPayment(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
                >
                  &times;
                </button>
                <h2 className="text-3xl font-bold mb-4">Supplier Payment Details</h2>
                
                <div className="mb-4">
                  <label className="block font-semibold">Payment Receipt ID</label>
                  <input type="text" value={selectedPayment.supplier_payment_id} readOnly className="w-full px-4 py-2 border rounded-lg" />
                </div>

                <div className="mb-4">
                  <label className="block font-semibold">Supplier Code</label>
                  <input type="text" value={selectedPayment.supplier_id?.supplier_code || "Unknown"} readOnly className="w-full px-4 py-2 border rounded-lg" />
                </div>

                <div className="mb-4">
                  <label className="block font-semibold">Supplier Name</label>
                  <input type="text" value={selectedPayment.supplier_id?.supplier_name || "Unknown"} readOnly className="w-full px-4 py-2 border rounded-lg" />
                </div>

                <div className="mb-4">
                  <label className="block font-semibold">Amount</label>
                  <input type="text" value={selectedPayment.amount || 0} readOnly className="w-full px-4 py-2 border rounded-lg" />
                </div>

                <div className="mb-4">
                  <label className="block font-semibold">Date</label>
                  <input type="text" value={selectedPayment.payment_date || ""} readOnly className="w-full px-4 py-2 border rounded-lg" />
                </div>

                <h3 className="text-lg font-semibold mt-4 mb-2">Invoices</h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={(selectedPayment.invoices || []).map((invoice, idx) => ({
                    id: idx + 1,
                    invoiceID: invoice.invoice_id?.invoice_id,
                    amount_applied: invoice.amount_applied,
                    }))}
                    columns={[
                      { field: "id", headerName: "No.", flex: 0.3 },
                      { field: "invoiceID", headerName: "Invoice ID", flex: 1 },
                      { field: "amount_applied", headerName: "Applied Amount (LKR)", flex: 1, headerAlign: "right", align: "right", renderCell: (params) => Number(params.value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) },
                    ]}
                    pageSize={16}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplierPayments;
